import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReportsService } from '../../services/reports.service';

interface TerritorySlice {
	territory_id: string | null;
	territory_name: string;
	sales: number;
	pct_of_total: number;
}

interface SalesmanTerritoryStats {
	success: boolean;
	error?: string;
	salesman?: string;
	datefrom?: string;
	dateto?: string;
	prevfrom?: string;
	prevto?: string;
	mtdSales?: number;
	mtdSalesLY?: number;
	mtdPct?: number | null;
	activeCustomers?: number;
	activeCustomersLY?: number;
	topTerritory?: { territory_id: string; territory_name: string; sales: number } | null;
	topBrand?: { manufacturer_id: number; name: string; sales: number } | null;
	byTerritory?: TerritorySlice[];
}

/** Same UX as Sales Performance (`app-salesman-kpis`): month pickers + rolling MTD. */
@Component({
	selector: 'app-salesman-territory-stats',
	templateUrl: './salesman-territory-stats.component.html',
	styleUrls: ['./salesman-territory-stats.component.scss']
})
export class SalesmanTerritoryStatsComponent implements OnInit, OnDestroy {
	@Output() viewData = new EventEmitter<string>();

	stats: SalesmanTerritoryStats | null = null;
	loading = true;
	error = '';

	/** YYYY-MM — aligned with `app-salesman-kpis` (primary = reporting month when Apply is used). */
	kpiPrimaryYm = '';
	kpiCompareYm = '';

	/**
	 * `rolling`: API `ThisMonth` (MTD-style window from server).
	 * `month`: API `Custom` = full calendar month of `kpiPrimaryYm` (compare month is cosmetic; % vs prior year).
	 */
	private mode: 'rolling' | 'month' = 'rolling';

	private loadSub?: Subscription;

	constructor(private reportsService: ReportsService) {}

	ngOnInit(): void {
		const y = this.suggestedYmPair();
		this.kpiPrimaryYm = y.primary;
		this.kpiCompareYm = y.compare;
		this.mode = 'rolling';
		this.load();
	}

	ngOnDestroy(): void {
		this.loadSub?.unsubscribe();
	}

	reload(): void {
		this.load();
	}

	applyCustomMonths(): void {
		if (!this.kpiPrimaryYm || !this.kpiCompareYm) {
			return;
		}
		this.mode = 'month';
		this.load();
	}

	resetToRollingMtd(): void {
		const y = this.suggestedYmPair();
		this.kpiPrimaryYm = y.primary;
		this.kpiCompareYm = y.compare;
		this.mode = 'rolling';
		this.load();
	}

	private suggestedYmPair(): { primary: string; compare: string } {
		const now = new Date();
		const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		return {
			primary: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
			compare: `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`,
		};
	}

	private monthToYmdBounds(ym: string): { from: string; to: string } | null {
		if (!/^\d{4}-\d{2}$/.test(ym)) {
			return null;
		}
		const [ys, ms] = ym.split('-');
		const y = parseInt(ys, 10);
		const mo = parseInt(ms, 10) - 1;
		if (mo < 0 || mo > 11) {
			return null;
		}
		const fromD = new Date(y, mo, 1);
		const toD = new Date(y, mo + 1, 0);
		return {
			from: this.formatYmdLocal(fromD),
			to: this.formatYmdLocal(toD),
		};
	}

	private formatYmdLocal(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private buildPayload(): any {
		if (this.mode === 'rolling') {
			return { dateRange: 'ThisMonth' };
		}
		const b = this.monthToYmdBounds(this.kpiPrimaryYm);
		if (!b) {
			return { dateRange: 'ThisMonth' };
		}
		return {
			dateRange: 'Custom',
			fromDate: b.from,
			toDate: b.to,
		};
	}

	load(): void {
		if (this.mode === 'month') {
			const b = this.monthToYmdBounds(this.kpiPrimaryYm);
			if (!b) {
				return;
			}
		}

		this.loadSub?.unsubscribe();
		this.loading = true;
		this.error = '';
		this.loadSub = this.reportsService.getStrptSmStats(this.buildPayload()).subscribe({
			next: (r: any) => {
				if (r && r.success) {
					this.stats = r as SalesmanTerritoryStats;
				} else {
					this.stats = null;
					this.error = r && r.error ? String(r.error) : 'Unable to load territory stats';
				}
				this.loading = false;
			},
			error: () => {
				this.stats = null;
				this.error = 'Unable to load territory stats';
				this.loading = false;
			}
		});
	}

	openFullReport(): void {
		this.viewData.emit('Salesman Territory');
	}

	get pctClass(): string {
		const p = this.stats?.mtdPct;
		if (p === null || p === undefined) return 'text-muted';
		if (p > 0) return 'text-success';
		if (p < 0) return 'text-danger';
		return 'text-muted';
	}

	get pctIcon(): string {
		const p = this.stats?.mtdPct;
		if (p === null || p === undefined) return '';
		if (p > 0) return 'fa-long-arrow-up';
		if (p < 0) return 'fa-long-arrow-down';
		return '';
	}

	customerDelta(): number {
		const cur = this.stats?.activeCustomers ?? 0;
		const prev = this.stats?.activeCustomersLY ?? 0;
		return cur - prev;
	}

	customerDeltaClass(): string {
		const d = this.customerDelta();
		if (d > 0) return 'text-success';
		if (d < 0) return 'text-danger';
		return 'text-muted';
	}
}
